import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { cn } from '@/libs/utils'
import { PlusIcon, SendIcon, ZapIcon, ArrowLeftIcon } from 'lucide-react'
import { useOrganizationServiceAdminAllOrganizationsQuery } from 'zma-react-sdk/organization-service'
import { useTemplateServiceCreateTemplateMutation } from 'zma-react-sdk/template-service'
import { useUserServiceAdminAllTenantQuery } from 'zma-react-sdk/user-service'
import { useToast } from '@/hooks/use-toast'
import { useTranslation } from '@/hooks/use-translation'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'

// Predefined variables
const PREDEFINED_VARIABLES = [
  'customer_name',
  'order_id',
  'amount',
  'delivery_date',
  'store_name',
  'phone',
  'promo_code',
]

const SAMPLE_VALUES: Record<string, string> = {
  customer_name: 'Nguyen Van A',
  order_id: 'ORD-2025-1125',
  amount: '150,000',
  delivery_date: '25/11/2025',
  store_name: 'Zalo Shop HCM',
  phone: '0909123456',
  promo_code: 'WELCOME10',
}

export function ZNSTemplateEditor() {
  const [templateType, setTemplateType] = useState<'ZNS' | 'SMS'>('ZNS')
  const [templateName, setTemplateName] = useState('')
  const [templateCode, setTemplateCode] = useState('')
  const [content, setContent] = useState('')
  const [variables, setVariables] = useState<string[]>([])
  const [previewValues, setPreviewValues] = useState<Record<string, string>>({})
  const [openInsertVar, setOpenInsertVar] = useState(false)
  const [newVarName, setNewVarName] = useState('')
  const [invalidVars, setInvalidVars] = useState<string[]>([])
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>(
    () => {
      // Try to get from localStorage (from admin template list page)
      if (typeof window !== 'undefined') {
        return localStorage.getItem('admin_templates_orgId') || ''
      }
      return ''
    }
  )
  const [selectedTenantId, setSelectedTenantId] = useState<string>(() => {
    // Try to get from localStorage (from admin template list page)
    if (typeof window !== 'undefined') {
      return localStorage.getItem('admin_templates_tenantId') || ''
    }
    return ''
  })
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const { t } = useTranslation()
  const { toast } = useToast()
  const navigate = useNavigate()

  // Fetch organizations
  const { data: orgData } = useOrganizationServiceAdminAllOrganizationsQuery({
    variables: { input: { limit: 100, skip: 0 } },
  })

  // Fetch tenants for selected organization
  const { data: tenantData } = useUserServiceAdminAllTenantQuery({
    variables: {
      input: { limit: 100, skip: 0 },
      organizationId: selectedOrganizationId || '',
    },
    skip: !selectedOrganizationId,
  })

  // Create template mutation
  const [createTemplate, { loading }] =
    useTemplateServiceCreateTemplateMutation({
      onCompleted: (data) => {
        toast({
          title: t('admin.templateCreated'),
          description: `Template "${data.templateServiceCreateTemplate?.name}" has been created successfully.`,
        })
        // Keep org and tenant in localStorage for table page
        if (typeof window !== 'undefined') {
          localStorage.setItem('admin_templates_orgId', selectedOrganizationId)
          localStorage.setItem('admin_templates_tenantId', selectedTenantId)
        }
        navigate({ to: '/admin/templates' as any })
      },
      onError: (error) => {
        toast({
          title: t('common.error'),
          description: error.message,
          variant: 'destructive',
        })
      },
    })

  // Extract variables from content
  const extractVariables = useCallback((text: string) => {
    const regex = /{{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*}}/g
    const matches = text.match(regex) || []
    return [...new Set(matches.map((m) => m.replace(/{{\s*|\s*}}/g, '')))]
  }, [])

  // Validate variable names
  const validateVariables = useCallback((vars: string[]) => {
    return vars.filter((v) => !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(v))
  }, [])

  // Update variables when content changes
  useEffect(() => {
    const vars = extractVariables(content)
    setVariables(vars)
    setInvalidVars(validateVariables(vars))

    // Initialize preview values
    setPreviewValues((prev) => {
      const initialPreview = { ...prev }
      vars.forEach((v) => {
        if (!(v in initialPreview)) {
          initialPreview[v] = SAMPLE_VALUES[v] || `sample_${v}`
        }
      })
      return initialPreview
    })
  }, [content, extractVariables, validateVariables])

  // Handle smart variable deletion
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      (e.key !== 'Backspace' && e.key !== 'Delete') ||
      textareaRef.current?.selectionStart !== textareaRef.current?.selectionEnd
    ) {
      return
    }

    const cursorPos = textareaRef.current!.selectionStart
    const varRegex = /{{\s*[a-zA-Z_][a-zA-Z0-9_]*\s*}}/g
    let match
    let targetMatch: RegExpExecArray | null = null

    while ((match = varRegex.exec(content)) !== null) {
      if (
        cursorPos >= match.index &&
        cursorPos <= match.index + match[0].length
      ) {
        targetMatch = match
        break
      }
    }

    if (!targetMatch) return

    e.preventDefault()
    const start = targetMatch.index
    const end = start + targetMatch[0].length
    let newStart = start
    let newEnd = end

    if (start > 0 && content[start - 1] === ' ') newStart = start - 1
    if (end < content.length && content[end] === ' ') newEnd = end + 1

    const newValue = content.substring(0, newStart) + content.substring(newEnd)
    setContent(newValue)

    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.setSelectionRange(newStart, newStart)
      }
    }, 0)
  }

  // Insert variable at cursor position
  const insertVariable = (varName: string) => {
    if (!textareaRef.current) return

    const start = textareaRef.current.selectionStart
    const end = textareaRef.current.selectionEnd
    const before = content.substring(0, start)
    const after = content.substring(end)
    const newValue = `${before}{{${varName}}}${after}`

    setContent(newValue)
    setOpenInsertVar(false)

    setTimeout(() => {
      if (textareaRef.current) {
        const newPos = start + varName.length + 4
        textareaRef.current.setSelectionRange(newPos, newPos)
        textareaRef.current.focus()
      }
    }, 0)
  }

  // Update preview value
  const handlePreviewChange = (varName: string, value: string) => {
    setPreviewValues((prev) => ({ ...prev, [varName]: value }))
  }

  // Render preview
  const renderPreview = () => {
    let previewText = content
    variables.forEach((v) => {
      previewText = previewText.replace(
        new RegExp(`{{\\s*${v}\\s*}}`, 'g'),
        previewValues[v] || `{{${v}}}`
      )
    })
    return previewText
  }

  // Handle template submission
  const handleSubmit = async () => {
    if (!templateName || !templateCode || !content || !selectedTenantId) {
      toast({
        title: t('common.error'),
        description: 'Please fill in all required fields',
        variant: 'destructive',
      })
      return
    }

    if (invalidVars.length > 0) {
      toast({
        title: t('common.error'),
        description: `Invalid variable names: ${invalidVars.join(', ')}`,
        variant: 'destructive',
      })
      return
    }

    await createTemplate({
      variables: {
        input: {
          tenantId: selectedTenantId,
          name: templateName,
          templateCode,
          type: templateType,
          content,
          variables,
        },
      },
    })
  }

  return (
    <div className='flex min-h-screen w-full flex-col bg-gradient-to-br from-emerald-50/30 to-blue-50/30 p-4 md:p-6'>
      {/* Header */}
      <header className='mb-6'>
        <div className='flex items-center gap-3'>
          <Button
            variant='ghost'
            size='icon'
            onClick={() => navigate({ to: '/admin/templates' as any })}
          >
            <ArrowLeftIcon className='h-5 w-5' />
          </Button>
          <h1 className='bg-gradient-to-r from-emerald-600 to-blue-600 bg-clip-text text-2xl font-bold text-transparent'>
            {t('admin.createTemplate')} -{' '}
            {templateType === 'ZNS' ? 'Zalo ZNS' : 'SMS'} Template Editor
          </h1>
        </div>
      </header>

      <div className='space-y-6'>
        {/* Organization and Tenant Selection */}
        <Card>
          <CardHeader>
            <CardTitle>{t('admin.selectOrganizationAndTenant')}</CardTitle>
            <CardDescription>
              {t('admin.selectOrgTenantDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className='grid gap-4 md:grid-cols-3'>
            <div className='space-y-2'>
              <Label>{t('admin.type')} *</Label>
              <Select
                value={templateType}
                onValueChange={(value: 'ZNS' | 'SMS') => setTemplateType(value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='ZNS'>ZNS (Zalo)</SelectItem>
                  <SelectItem value='SMS'>SMS</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className='space-y-2'>
              <Label>{t('admin.organization')} *</Label>
              <Select
                value={selectedOrganizationId}
                onValueChange={setSelectedOrganizationId}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('admin.selectOrganization')} />
                </SelectTrigger>
                <SelectContent>
                  {orgData?.organizationServiceAdminAllOrganizations?.map(
                    (org) => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.name}
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className='space-y-2'>
              <Label>{t('admin.tenant')} *</Label>
              <Select
                value={selectedTenantId}
                onValueChange={setSelectedTenantId}
                disabled={!selectedOrganizationId}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('admin.selectTenant')} />
                </SelectTrigger>
                <SelectContent>
                  {tenantData?.userServiceAdminAllTenant?.map((tenant) => (
                    <SelectItem key={tenant.id} value={tenant.id}>
                      {tenant.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <div className='grid gap-6 md:grid-cols-2'>
          {/* Editor */}
          <Card>
            <CardHeader>
              <CardTitle className='flex items-center gap-2'>
                <ZapIcon className='h-5 w-5 text-emerald-600' />
                {t('admin.templateEditor')}
              </CardTitle>
              <CardDescription>
                {t('admin.configureYourMessageTemplate')}
              </CardDescription>
            </CardHeader>
            <CardContent className='space-y-4'>
              <div className='space-y-2'>
                <Label>{t('admin.templateName')} *</Label>
                <Input
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder='Order Confirmation'
                />
              </div>

              <div className='space-y-2'>
                <Label>{t('admin.templateCode')} *</Label>
                <Input
                  value={templateCode}
                  onChange={(e) => setTemplateCode(e.target.value)}
                  placeholder='ORDER_CONFIRM'
                />
              </div>

              <div className='space-y-2'>
                <div className='flex items-center justify-between'>
                  <Label>{t('admin.content')} *</Label>
                  <Popover open={openInsertVar} onOpenChange={setOpenInsertVar}>
                    <PopoverTrigger asChild>
                      <Button
                        variant='outline'
                        size='sm'
                        className='h-8 gap-1 rounded-md border-dashed'
                      >
                        <PlusIcon className='h-3.5 w-3.5' />
                        <span>{t('admin.insertVariable')}</span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className='w-[300px] p-0' align='end'>
                      <Command>
                        <CommandInput
                          placeholder={t('admin.searchVariables')}
                        />
                        <CommandList className='max-h-[300px]'>
                          <CommandEmpty>
                            {t('admin.noVariablesFound')}
                          </CommandEmpty>
                          <CommandGroup heading={t('admin.createNew')}>
                            <div className='p-2'>
                              <div className='flex gap-2'>
                                <Input
                                  value={newVarName}
                                  onChange={(e) =>
                                    setNewVarName(e.target.value)
                                  }
                                  placeholder={t('admin.variableName')}
                                  className='h-8 text-xs'
                                />
                                <Button
                                  size='sm'
                                  onClick={() => {
                                    if (
                                      newVarName.trim() &&
                                      /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(
                                        newVarName
                                      )
                                    ) {
                                      insertVariable(newVarName)
                                      setNewVarName('')
                                    }
                                  }}
                                >
                                  {t('admin.add')}
                                </Button>
                              </div>
                            </div>
                          </CommandGroup>
                        </CommandList>
                        <div className='sticky bottom-0 border-t bg-background'>
                          <CommandGroup
                            heading={t('admin.predefinedVariables')}
                          >
                            {PREDEFINED_VARIABLES.map((variable) => (
                              <CommandItem
                                key={variable}
                                onSelect={() => insertVariable(variable)}
                              >
                                <span className='font-mono'>
                                  {'{{' + variable + '}}'}
                                </span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </div>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                <Textarea
                  ref={textareaRef}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder='Enter your message template...'
                  className='min-h-[140px] font-mono text-sm'
                />
                <p className='text-xs text-muted-foreground'>
                  {t('admin.useDoubleBracesForVariables')}:{' '}
                  {'{{variable_name}}'}
                </p>
              </div>

              <div className='space-y-2'>
                <Label>{t('admin.usedVariables')}</Label>
                <div className='flex flex-wrap gap-2'>
                  {variables.length === 0 ? (
                    <span className='text-sm italic text-muted-foreground'>
                      {t('admin.noVariablesUsed')}
                    </span>
                  ) : (
                    variables.map((v) => (
                      <Badge
                        key={v}
                        variant={
                          invalidVars.includes(v) ? 'destructive' : 'default'
                        }
                        className={cn(
                          'font-mono',
                          invalidVars.includes(v)
                            ? 'bg-red-100 text-red-800'
                            : 'bg-emerald-100 text-emerald-800'
                        )}
                      >
                        {v}
                        {invalidVars.includes(v) && (
                          <span className='ml-1'>!</span>
                        )}
                      </Badge>
                    ))
                  )}
                </div>
                {invalidVars.length > 0 && (
                  <p className='text-sm text-destructive'>
                    {t('admin.invalidVariableNames')}: {invalidVars.join(', ')}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Preview */}
          <Card>
            <CardHeader className='flex flex-row items-center justify-between'>
              <div>
                <CardTitle className='flex items-center gap-2'>
                  {t('admin.preview')}
                </CardTitle>
                <CardDescription>
                  {t('admin.howYourMessageAppearsOnZalo')}
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              <div className='space-y-6'>
                {/* Mobile preview */}
                <div className='rounded-xl border bg-white p-4 shadow-sm'>
                  <div className='mt-3 rounded-lg bg-muted p-4 font-sans'>
                    {renderPreview()}
                  </div>
                </div>

                {/* Test inputs */}
                <div className='space-y-4'>
                  <h3 className='text-sm font-medium'>
                    {t('admin.testVariables')}
                  </h3>
                  <div className='grid grid-cols-1 gap-4'>
                    {variables.map((v) => (
                      <div key={v} className='space-y-2'>
                        <Label className='text-xs'>{v}</Label>
                        <Input
                          value={previewValues[v] || ''}
                          onChange={(e) =>
                            handlePreviewChange(v, e.target.value)
                          }
                          className='text-sm'
                        />
                      </div>
                    ))}
                    {variables.length === 0 && (
                      <p className='text-sm text-muted-foreground'>
                        {t('admin.noVariablesToTest')}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Action buttons */}
        <div className='flex justify-end gap-3'>
          <Button
            variant='outline'
            onClick={() => navigate({ to: '/admin/templates' as any })}
            disabled={loading}
            className='mr-2'
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading}
            className='gap-2 bg-emerald-600 hover:bg-emerald-700'
          >
            {loading ? (
              <>
                <SendIcon className='h-4 w-4 animate-pulse' />
                <span className='font-semibold'>{t('common.creating')}...</span>
              </>
            ) : (
              <>
                <SendIcon className='h-4 w-4' />
                {t('admin.createTemplate')}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}
